import React, { useState, FormEvent, useEffect, useRef } from "react";
import { useElements, useStripe, CardElement } from "@stripe/react-stripe-js";
import { Grid, Typography, OutlinedInput, Box } from "@mui/material";

import Button from "../../../../components/Form/Button";

type Props = {
  clientSecret: string;
  defaultEmail?: string;
  defaultName?: string;
  onSaved: (paymentMethodId: string, brand?: string, last4?: string) => void;
  onCancel: () => void;
};

const StripeCardForm: React.FC<Props> = ({
  clientSecret,
  defaultEmail,
  defaultName,
  onSaved,
  onCancel
}) => {
  try {

    const stripe = useStripe();
    const elements = useElements();

    const [holderName, setHolderName] = useState(defaultName || "");
    const [email, setEmail] = useState(defaultEmail || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

  const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (stripe && elements && !isInitialized) {
      setIsInitialized(true);
    }
  }, [stripe, elements, isInitialized, instanceId]);

  const handleSave = async () => {
    if (!stripe || !elements) {
      return;
    }
    setSaving(true);
    setError(null);

    if (!clientSecret) {
      setError("Client secret not available");
      setSaving(false);
      return;
    }

    const card = elements.getElement(CardElement);
    if (!card) {
      setSaving(false);
      return;
    }

    try {
      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
          billing_details: {
            name: holderName || undefined,
            email: email || undefined
          },
        },
      });

      if (error) {
        setError(error.message || "Card confirmation failed");
        setSaving(false);
        return;
      }

      if (!setupIntent) {
        setError("SetupIntent not returned");
        setSaving(false);
        return;
      }

      const paymentMethodId = typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method?.id;

      if (!paymentMethodId) {
        setError("Payment method not created");
        setSaving(false);
        return;
      }

      const paymentMethodObj = typeof setupIntent.payment_method === 'object'
        ? setupIntent.payment_method
        : null;

      const brand = paymentMethodObj?.card?.brand;
      const last4 = paymentMethodObj?.card?.last4;

      setSuccess(true);
      setSaving(false);

      setTimeout(() => {
        onSaved(paymentMethodId, brand, last4);
      }, 800);
    } catch (err: any) {
      setError(err.message || "Card confirmation failed");
      setSaving(false);
    }
  };

  if (!clientSecret) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div style={{ color: "#D92D20", fontSize: "14px" }}>
          Error: Payment form not loaded properly
        </div>
      </Box>
    );
  }

  if (!stripe || !elements || !isInitialized) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div style={{ color: "#667085", fontSize: "14px" }}>
          Loading payment form...
        </div>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        handleSave();
      }}
      sx={{ width: "100%" }}
    >
      {error && (
        <Box
          sx={{
            color: "#D92D20",
            fontSize: "14px",
            marginBottom: 2,
            padding: "12px 16px",
            backgroundColor: "#FEF3F2",
            borderRadius: "8px",
            border: "1px solid #FECDCA",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {error}
        </Box>
      )}

      {success && (
        <Box
          sx={{
            color: "#027A48",
            fontSize: "14px",
            marginBottom: 2,
            padding: "12px 16px",
            backgroundColor: "#ECFDF3",
            borderRadius: "8px",
            border: "1px solid #6CE9A6",
            display: "flex",
            alignItems: "center",
            gap: 1,
            animation: "fadeIn 0.3s ease-in",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(-10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#027A48" }}>
              Payment method confirmed successfully!
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#027A48", mt: 0.5 }}>
              Saving your payment information...
            </Typography>
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              mb: 0.75,
            }}
          >
            Name on card <span style={{ color: "#D92D20" }}>*</span>
          </Typography>
          <OutlinedInput
            fullWidth
            value={holderName}
            onChange={(e) => setHolderName(e.target.value)}
            placeholder="Name"
            required
            disabled={saving || success}
            sx={{
              height: "40px",
              fontFamily: "Manrope",
              fontSize: "16px",
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              mb: 0.75,
            }}
          >
            Card Details <span style={{ color: "#D92D20" }}>*</span>
          </Typography>
          <Box
            sx={{
              height: "40px",
              px: 1.5,
              py: "10px",
              border: "1px solid #D0D5DD",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: (saving || success) ? "#F9FAFB" : "#FFFFFF",
              opacity: (saving || success) ? 0.6 : 1,
              pointerEvents: (saving || success) ? "none" : "auto",
              "&:hover": {
                borderColor: "#98A2B3",
              },
              "&:focus-within": {
                borderColor: "#0055A4",
                borderWidth: "2px",
                px: "11px",
              },
              "& .StripeElement": {
                width: "100%",
              },
            }}
          >
            <CardElement
              options={{
                hidePostalCode: true,
                disabled: saving || success,
                style: {
                  base: {
                    fontSize: "16px",
                    fontFamily: "Manrope, sans-serif",
                    color: "#101828",
                    "::placeholder": {
                      color: "#98A2B3",
                    },
                  },
                  invalid: {
                    color: "#D92D20",
                  },
                },
              }}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Manrope",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "20px",
              color: "#0055A4",
              mb: 0.75,
            }}
          >
            Address <span style={{ color: "#D92D20" }}>*</span>
          </Typography>
          <OutlinedInput
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Search address"
            required
            disabled={saving || success}
            sx={{
              height: "40px",
              fontFamily: "Manrope",
              fontSize: "16px",
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
              },
            }}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}>
        <Button
          type="button"
          onClick={onCancel}
          disabled={saving || success}
          sx={{
            px: 3,
            py: 1,
            fontFamily: "Manrope",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            color: "#344054",
            textTransform: "none",
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            "&:hover": {
              bgcolor: "#F9FAFB",
              border: "1px solid #D0D5DD",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || saving || success}
          sx={{
            px: 3,
            py: 1,
            fontFamily: "Manrope",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "20px",
            color: "#FFFFFF",
            textTransform: "none",
            bgcolor: success ? "#027A48" : "#0055A4",
            borderRadius: "8px",
            "&:hover": {
              bgcolor: success ? "#027A48" : "#003d7a",
            },
            "&.Mui-disabled": {
              bgcolor: "#D0D5DD",
              color: "#98A2B3",
            },
          }}
        >
          {success ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <span>✓</span>
              <span>Confirmed</span>
            </Box>
          ) : saving ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: "14px",
                  height: "14px",
                  border: "2px solid #FFFFFF",
                  borderTopColor: "transparent",
                  borderRadius: "50%",
                  animation: "spin 0.6s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <span>Processing...</span>
            </Box>
          ) : (
            "Save"
          )}
        </Button>
      </Box>
    </Box>
  );
  } catch (error: any) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <div style={{ color: "#D92D20", fontSize: "14px" }}>
          Error rendering payment form: {error.message}
        </div>
      </Box>
    );
  }
};

export default StripeCardForm;
